import Maybe from 'true-myth/maybe';
import Result from 'true-myth/result';
import { fromResult } from 'true-myth/toolbelt';

import {
    competenciesSchema,
    jobProfileLegacySchema,
    jobProfilesOptionsResponseSchema,
} from '../../job-profile/job-profile-schema';

import type { ValidatingHttpFetcherWithBaseUrl } from '../../../../shared/source/validating-http-fetcher';
import type { LoaderFunction } from 'react-router';

const fetchJobProfileDescriptionSections = async (
    validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl,
): Promise<Maybe<readonly string[]>> => {
    const result = await validatingCobrainerApiFetcher.get(jobProfilesOptionsResponseSchema, {
        pathname: `/job-profiles/options`,
    });

    if (result.isErr) {
        return Maybe.nothing();
    }

    const data = fromResult(result);

    return data.map((options) => options.structuredTags);
};

export const createJobProfilePageLoader = (
    validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl,
): LoaderFunction => {
    return async (loaderArguments) => {
        const jobProfileId = Maybe.of(loaderArguments.params['jobProfileId']);
        const mode = Maybe.of(loaderArguments.params['mode']);

        if (jobProfileId.isNothing) {
            return Result.err(new Error('No id path parameter present'));
        }

        const [jobProfile, competencies, structuredSections] = await Promise.all([
            validatingCobrainerApiFetcher.get(jobProfileLegacySchema, {
                pathname: `/job-profiles/${jobProfileId.value}`,
            }),
            validatingCobrainerApiFetcher.get(competenciesSchema, {
                pathname: '/skills/competencies',
            }),
            fetchJobProfileDescriptionSections(validatingCobrainerApiFetcher),
        ]);

        const isInEditMode =
            mode.equals(Maybe.of('edit')) && jobProfile.map((profile) => profile.actions?.canEdit).unwrapOr(false);

        return Result.ok({
            competencies: fromResult(competencies),
            isInEditMode,
            jobProfile: fromResult(jobProfile),
            structuredSections,
        });
    };
};
